import React, { useEffect, useState, useRef } from 'react'; // Added useRef
import Dialog from '@material-ui/core/Dialog';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from '@material-ui/core/styles';
import { MdClose } from 'react-icons/md';

const useStyles = makeStyles({
    modalPaper: {
        borderRadius: 0,
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        margin: '16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    modalBackdrop: {
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(3, 7, 18, 0.5)',
    },
    lightboxPaper: {
        background: 'transparent',
        boxShadow: 'none',
        maxWidth: '1400px',
        width: '100%',
        margin: '24px',
        overflow: 'visible',
    },
    lightboxBackdrop: {
        backdropFilter: 'none',
        backgroundColor: 'rgba(3, 7, 18, 0.88)',
    },
});

const ImageLightbox = ({ src, alt, open, onClose }) => {
    const classes = useStyles();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 180 }}
            PaperProps={{
                className: classes.lightboxPaper,
                style: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    overflow: 'visible'
                }
            }}
        >
            <div className="flex flex-col items-center" onClick={onClose}>
                <img
                    src={src}
                    alt={alt}
                    className="w-full h-auto max-h-[90vh] object-contain rounded-xl shadow-2xl border border-white/10"
                    onClick={(e) => e.stopPropagation()}
                />
                <p className="text-xs text-white/35 mt-3 select-none">
                    Click outside or press Esc to close
                </p>
            </div>
        </Dialog>
    );
};

const formatContextText = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    return lines.map((line, lineIndex) => {
        const parts = line.split(/(\*\*.*?\*\*)/g);

        return (
            <React.Fragment key={lineIndex}>
                {parts.map((part, partIndex) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return (
                            <strong key={partIndex} className="font-bold text-gray-900">
                                {part.slice(2, -2)}
                            </strong>
                        );
                    }
                    return <span key={partIndex}>{part}</span>;
                })}
                {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
        );
    });
};

const PageInfoModal = ({ isOpen, onClose, title, steps, tabs }) => {
    const classes = useStyles();
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    // 1. Create a reference for the scrolling container
    const scrollContainerRef = useRef(null);

    const displaySteps = tabs ? tabs[selectedTabIndex]?.steps : (steps || []);

    // Reset to first tab when modal opens
    useEffect(() => {
        setSelectedTabIndex(0);
    }, [isOpen]);

    // 2. Reset scroll position whenever the selected tab changes
    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [selectedTabIndex]);

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onClose}
                maxWidth={false}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 200 }}
                PaperProps={{ className: classes.modalPaper }}
                BackdropProps={{ className: classes.modalBackdrop }}
            >
                <div className="flex flex-shrink-0 items-center justify-between w-full px-3 py-1.5 bg-[#f8fafc] border-b border-slate-200">
                    <h2 className="text-slate-900 text-[14px] font-medium tracking-wide m-0">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-200/80 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                        title="Close"
                        aria-label="Close modal"
                    >
                        <MdClose size={16} />
                    </button>
                </div>

                {tabs && (
                    <div className="flex px-3 sm:px-5 border-b border-gray-100 bg-gray-50/30 overflow-x-auto overflow-y-hidden flex-shrink-0 scroll-thin min-h-[46px]">
                        {tabs.map((tab, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedTabIndex(index)}
                                className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${selectedTabIndex === index
                                    ? 'border-[#313C4C] text-[#313C4C]'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                {tab.title}
                            </button>
                        ))}
                    </div>
                )}

                {/* 3. Attach the ref to the scrollable container */}
                <div ref={scrollContainerRef} className="px-3 sm:px-5 py-3 sm:py-4 overflow-y-auto flex-1 space-y-3">
                    {displaySteps?.map((step, index) => (
                        <div
                            key={index}
                            className="flex gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border border-gray-100 bg-gray-50/60 hover:bg-slate-100/60 hover:border-slate-200 transition-all duration-200"
                        >
                            <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-slate-200 flex items-center justify-center mt-0.5">
                                <span className="text-xs font-bold text-[#313C4C]">{index + 1}</span>
                            </div>

                            <div className="flex-1 min-w-0">
                                <h3 className="text-[13px] sm:text-sm font-semibold text-gray-800 mb-1 leading-snug">
                                    {step.title.replace(/^\d+\.\s*/, '')}
                                </h3>

                                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                    {formatContextText(step.context)}
                                </p>

                                {step.imageSrc && (
                                    <div
                                        className="mt-3 rounded-lg overflow-hidden border border-gray-200/70 bg-white cursor-zoom-in relative group"
                                        onClick={() => setPreviewImage({ src: step.imageSrc, alt: step.title })}
                                    >
                                        <img
                                            src={step.imageSrc}
                                            alt={step.title}
                                            className="w-full h-auto object-contain max-h-[600px] transition-transform duration-200 group-hover:scale-[1.02]"
                                        />
                                        <div className="absolute inset-0 bg-[#313C4C]/0 group-hover:bg-[#313C4C]/10 transition-colors duration-200 flex items-center justify-center">
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-sm border border-gray-100">
                                                <svg className="w-3.5 h-3.5 text-[#313C4C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                </svg>
                                                <span className="text-xs font-medium text-gray-700">Click to expand</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex flex-shrink-0 items-center justify-between gap-2.5 w-full px-3 py-1.5 bg-slate-50 border-t border-slate-200">
                    <span className="text-[11px] text-slate-500">{displaySteps?.length} step{displaySteps?.length !== 1 ? 's' : ''}</span>
                    <button
                        onClick={onClose}
                        className="h-[28px] px-3.5 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded-none shadow-2xs transition-colors capitalize tracking-wider cursor-pointer"
                    >
                        Got it
                    </button>
                </div>
            </Dialog>

            <ImageLightbox
                open={!!previewImage}
                src={previewImage?.src}
                alt={previewImage?.alt}
                onClose={() => setPreviewImage(null)}
            />
        </>
    );
};

export default PageInfoModal;
